import React, { useEffect, useMemo, useState } from "react";
import styles from "./monster.module.css";
import { useDispatch, useSelector } from "react-redux";
import StarContainer from "../../components/star-container/star-container";
import Header from "../../components/header/header";
import ScrollUpButton from "../../components/buttons/scroll-up-button/scroll-up-button";
import Footer from "../../components/footer/footer";
import FilterSelect from "../../components/filter-select/filter-select";
import MonsterCard from "../../components/monster/monster-card";
import MonsterDetail from "../../components/monster/monster-detail";
import { initBattleContents } from "../../store/reducers/list/battleContentList";
import {
    getMajorCategoryList,
    getMiddleCategoryList,
    getSubCategoryList,
    getWaveList,
    getMonsterList,
    getBuffStatLabel,
    getBuffTargetLabel,
} from "../../functions/getBattleContentFunctions";
import { getUIText } from "../../translation";
import { v4 } from "uuid";

export default function Monster() {
    const theme = useSelector((state) => state.theme.value);
    const lang = useSelector((state) => state.lang.value);
    const dispatch = useDispatch();

    const battleContentState = useSelector((state) => state.battleContents);
    const { battleContents, monsters, buffs, status } = battleContentState;

    useEffect(() => {
        if (status === "idle") dispatch(initBattleContents());
    }, [status, dispatch]);

    const [major, setMajor] = useState();
    const [middle, setMiddle] = useState();
    const [sub, setSub] = useState();
    const [wave, setWave] = useState();

    const waveSuffix = getUIText(lang, "monster.select.waveSuffix");

    const majorCategoryList = useMemo(() => {
        if (!battleContents) return [];
        return getMajorCategoryList(battleContents, lang);
    }, [battleContents, lang]);

    const middleCategoryList = useMemo(() => {
        if (!battleContents || !major) return [];
        return getMiddleCategoryList(battleContents, major, lang);
    }, [battleContents, major, lang]);

    const subCategoryList = useMemo(() => {
        if (!battleContents || !major || !middle) return [];
        return getSubCategoryList(battleContents, major, middle, lang);
    }, [battleContents, major, middle, lang]);

    const waveList = useMemo(() => {
        if (!battleContents || !major || !middle || !sub) return [];
        return getWaveList(battleContents, major, middle, sub, waveSuffix);
    }, [battleContents, major, middle, sub, waveSuffix]);

    const monsterList = useMemo(() => {
        if (!battleContents || !monsters || !major || !middle || !sub || !wave) return [];
        return getMonsterList(battleContents, monsters, major, middle, sub, wave, lang);
    }, [battleContents, monsters, major, middle, sub, wave, lang]);

    // 상위 단계가 바뀔 때 하위 선택들을 같은 이벤트 안에서 한 번에 재계산해서 반영한다.
    // (단계별로 별도 useEffect에서 순차적으로 리셋하면, 새 상위값과 이전 하위값이 함께 존재하는
    //  렌더가 생겨 존재하지 않는 인덱스를 참조하다 에러가 나는 문제가 있었음)
    const handleMajorChange = (newMajor) => {
        const newMiddleList = getMiddleCategoryList(battleContents, newMajor, lang);
        const newMiddle = newMiddleList[0];
        const newSubList = newMiddle ? getSubCategoryList(battleContents, newMajor, newMiddle, lang) : [];
        const newSub = newSubList[0];
        const newWaveList = newSub ? getWaveList(battleContents, newMajor, newMiddle, newSub, waveSuffix) : [];

        setMajor(newMajor);
        setMiddle(newMiddle);
        setSub(newSub);
        setWave(newWaveList[0]);
    };

    const handleMiddleChange = (newMiddle) => {
        const newSubList = getSubCategoryList(battleContents, major, newMiddle, lang);
        const newSub = newSubList[0];
        const newWaveList = newSub ? getWaveList(battleContents, major, newMiddle, newSub, waveSuffix) : [];

        setMiddle(newMiddle);
        setSub(newSub);
        setWave(newWaveList[0]);
    };

    const handleSubChange = (newSub) => {
        const newWaveList = getWaveList(battleContents, major, middle, newSub, waveSuffix);

        setSub(newSub);
        setWave(newWaveList[0]);
    };

    // 데이터가 도착하면 1단계(컨텐츠 종류)부터 이어지는 하위 단계까지 한 번에 자동 선택
    useEffect(() => {
        if (!major && majorCategoryList.length > 0) handleMajorChange(majorCategoryList[0]);
    }, [majorCategoryList]);

    return (
        <div id="container" data-theme={theme} data-lang={lang}>
            {theme === "dark" && <StarContainer />}
            <Header title={getUIText(lang, "header.title.monster")} />

            <div className={styles["monster-container"]}>
                {majorCategoryList.length === 0 && <div className={styles["loading"]}>{getUIText(lang, "monster.loading")}</div>}
                <div className={styles["selectors"]}>
                    {major && (
                        <FilterSelect
                            id={"monster_major"}
                            selected={major}
                            list={majorCategoryList}
                            changeFilter={handleMajorChange}
                            isUseDispatch={false}
                            forceWidth={"100%"}
                        />
                    )}
                    {middle && (
                        <FilterSelect
                            id={"monster_middle"}
                            selected={middle}
                            list={middleCategoryList}
                            changeFilter={handleMiddleChange}
                            isUseDispatch={false}
                            forceWidth={"100%"}
                        />
                    )}
                    {sub && (
                        <FilterSelect
                            id={"monster_sub"}
                            selected={sub}
                            list={subCategoryList}
                            changeFilter={handleSubChange}
                            isUseDispatch={false}
                            forceWidth={"100%"}
                        />
                    )}
                    {wave && (
                        <FilterSelect
                            id={"monster_wave"}
                            selected={wave}
                            list={waveList}
                            changeFilter={setWave}
                            isUseDispatch={false}
                            forceWidth={"100%"}
                        />
                    )}
                </div>

                {sub && wave && monsterList.length > 0 && (
                    <>
                        <div className={styles["stage-info"]}>
                            {sub.buffs && sub.buffs.length > 0 && (
                                <div className={styles["stage-buffs"]}>
                                    <h4>{getUIText(lang, "monster.stage.buffs")}</h4>
                                    <div className={styles["buff-list"]}>
                                        {sub.buffs.map((buff) => (
                                            <div className={styles["buff-item"]} key={buff.id}>
                                                <span
                                                    className={`${styles["buff-target"]} ${buff.target?.type === "attr" ? styles[buff.target.value] : ""}`}
                                                >
                                                    {getBuffTargetLabel(lang, buff.target)}
                                                </span>
                                                {buff.stats.map((stat, index) => (
                                                    <span className={styles["buff-stat"]} key={index}>
                                                        {getBuffStatLabel(lang, buffs, stat.type)} {stat.value > 0 ? "+" : ""}
                                                        {stat.value / 100}%
                                                    </span>
                                                ))}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            <div className={styles["recommended-power"]}>
                                <span className={styles["label"]}>{getUIText(lang, "monster.stage.recommendedPower")}</span>
                                <span className={styles["value"]}>{sub.battlePower?.toLocaleString("ko-KR")}</span>
                            </div>
                        </div>

                        <div className={styles["monster-cards"]}>
                            {monsterList.map((monster, index) => (
                                <MonsterCard lang={lang} monster={monster} key={`monster_card_${index}_${v4()}`} />
                            ))}
                        </div>

                        <div className={styles["monster-details"]}>
                            {monsterList.map((monster, index) => (
                                <MonsterDetail lang={lang} monster={monster} key={`monster_detail_${index}_${v4()}`} />
                            ))}
                        </div>
                    </>
                )}
            </div>

            <ScrollUpButton />
            <Footer />
        </div>
    );
}
